/**
 * MembersPage (멤버 관리 화면)
 *
 * 멤버 목록 조회 및 관리를 수행하는 페이지
 *
 * 책임:
 * - 멤버 목록 표시 (역할 포함)
 * - 멤버 초대 (추가)
 * - 역할 변경 (Owner ↔ Member)
 * - 멤버 제거 (Owner 최소 1명 보장)
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserCRUD } from '../hooks/useUserCRUD'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { InviteMemberModal } from '../components/InviteMemberModal'
import {
  canChangeUserRole,
  canRemoveUser,
  checkMinimumOwners,
} from '../utils/userPermissions'
import { useToast } from '@/shared/components/ui/Toast'
import type { User, UserRole } from '../types/User'

export function MembersPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const { data, loading, error, loadAll, create, update, remove } =
    useUserCRUD()
  const { currentUser, loading: currentUserLoading } = useCurrentUser()

  const [showInviteModal, setShowInviteModal] = useState(false)

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    loadAll()
  }, [])

  /**
   * 멤버 초대 시작
   */
  const handleInvite = () => {
    setShowInviteModal(true)
  }

  /**
   * 역할 변경 (권한 검증 포함)
   *
   * @fix BUG-009: 권한 검증 로직 추가
   */
  const handleChangeRole = async (user: User) => {
    // 현재 유저 확인
    if (!currentUser) {
      toast.error('현재 사용자 정보를 불러올 수 없습니다')
      return
    }

    const newRole: UserRole = user.role === 'owner' ? 'member' : 'owner'

    // 권한 검증
    if (!canChangeUserRole(currentUser, user, newRole)) {
      toast.error('권한이 없습니다. Owner만 역할을 변경할 수 있습니다.')
      return
    }

    // 최소 Owner 수 검증
    if (user.role === 'owner' && newRole === 'member') {
      if (!checkMinimumOwners(data, user, 'demote')) {
        toast.warning('최소 1명의 Owner가 필요합니다')
        return
      }
    }

    const confirmMsg =
      newRole === 'owner'
        ? `${user.name}님을 Owner로 승격하시겠습니까?`
        : `${user.name}님을 Member로 변경하시겠습니까?`

    if (!confirm(confirmMsg)) return

    try {
      await update(user.id, { role: newRole }, currentUser)
      toast.success('역할이 변경되었습니다')
      await loadAll()
    } catch (err) {
      console.error('역할 변경 실패:', err)
      toast.error(`역할 변경 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 멤버 제거 (권한 검증 포함)
   *
   * @fix BUG-009: 권한 검증 로직 추가
   */
  const handleRemove = async (user: User) => {
    // 현재 유저 확인
    if (!currentUser) {
      toast.error('현재 사용자 정보를 불러올 수 없습니다')
      return
    }

    // 권한 검증
    if (!canRemoveUser(currentUser, user)) {
      if (currentUser.role !== 'owner') {
        toast.error('권한이 없습니다. Owner만 멤버를 제거할 수 있습니다.')
      } else {
        toast.warning('Owner는 제거할 수 없습니다')
      }
      return
    }

    if (!confirm(`${user.name}님을 멤버에서 제거하시겠습니까?`)) return

    try {
      await remove(user.id, currentUser)
      toast.success('제거되었습니다')
      await loadAll()
    } catch (err) {
      console.error('멤버 제거 실패:', err)
      toast.error(`제거 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 초대 저장
   */
  const handleSaveInvite = async (input: Omit<User, 'id'>) => {
    try {
      await create(input)
      toast.success('초대되었습니다')
      await loadAll()
      setShowInviteModal(false)
    } catch (err) {
      console.error('초대 실패:', err)
      toast.error(`초대 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 로딩 상태 (BUG-009: currentUserLoading 추가)
   */
  if ((loading || currentUserLoading) && data.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">멤버를 불러오는 중...</p>
        </div>
      </div>
    )
  }

  /**
   * 에러 상태
   */
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center mb-4">
            <span className="text-6xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
            데이터를 불러올 수 없습니다
          </h2>
          <p className="text-red-600 dark:text-red-400 text-sm text-center mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  /**
   * 메인 컨텐츠
   */
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* 헤더 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
        <button
          onClick={() => navigate('/settings')}
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-2 flex items-center gap-1"
        >
          ← 뒤로 가기
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">멤버 관리</h1>
      </div>

      {/* 멤버 목록 */}
      <div className="px-4 py-6 space-y-3">
        {data.length === 0 ? (
          <div className="text-center py-12">
            <span className="text-6xl block mb-4">👥</span>
            <p className="text-gray-600 dark:text-gray-400 mb-4">등록된 멤버가 없습니다</p>
            <button
              onClick={handleInvite}
              className="px-6 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              멤버 초대하기
            </button>
          </div>
        ) : (
          data.map((user) => (
            <div
              key={user.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-lg">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {user.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        user.role === 'owner'
                          ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {user.role === 'owner' ? 'Owner' : 'Member'}
                    </span>
                    {!user.isActive && (
                      <span className="text-xs text-red-600 dark:text-red-400">비활성</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {/* BUG-009: 권한 기반 버튼 표시 */}
                {currentUser && canChangeUserRole(currentUser, user) && (
                  <button
                    onClick={() => handleChangeRole(user)}
                    className="px-3 py-1 text-sm bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded transition-colors"
                  >
                    {user.role === 'owner' ? 'Member로' : 'Owner로'}
                  </button>
                )}
                {currentUser && canRemoveUser(currentUser, user) && (
                  <button
                    onClick={() => handleRemove(user)}
                    className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded transition-colors"
                  >
                    제거
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* 초대 버튼 (고정) */}
      {data.length > 0 && (
        <div className="fixed bottom-20 right-4">
          <button
            onClick={handleInvite}
            className="w-14 h-14 bg-primary-600 dark:bg-primary-500 text-white rounded-full shadow-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors flex items-center justify-center text-2xl"
          >
            +
          </button>
        </div>
      )}

      {/* 멤버 초대 모달 */}
      <InviteMemberModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSave={handleSaveInvite}
      />
    </div>
  )
}
